import { AccessorKeyColumnDef, ColumnHelper } from '@tanstack/react-table'
import { Campaign } from 'models/campaign'
import { CompanyDistributor, CompanyGPO } from 'models/companies'
import { Deal } from 'models/deals'
import { Preferences } from 'models/preferences'
import { Tag } from 'models/tags'
import { DataTableColumnSort } from '../../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  AccountOwnerCell,
  CampaignsCell,
  DistributorsCell,
  GPOsCell,
  SaleStagesCellFromDeals,
  TaglistCell,
} from '../../../FbUI/StagePill'
import { COMMON_COLUMNS } from '../CommonColumnsInfo'
import { createTableColumnHelper } from '../commonColumnHelpers'

// Utility function to extract account owner IDs from deals
const getAccountOwnerIdsFromDeals = (deals?: Deal[] | null): number[] => {
  if (!deals) return []
  const accountOwnerIds = deals
    .map((deal) => deal.account_owner)
    .filter((id): id is number => id !== null && id !== undefined)
    .filter((id, index, arr) => arr.indexOf(id) === index) // remove duplicates
  return accountOwnerIds
}

interface RequiredFields {
  contact_count?: number
  note_count?: number
  deal_count?: number
  campaign_attributed_deal_count?: number
  taglist?: Tag[] | null
  distributors?: CompanyDistributor[]
  gpos?: CompanyGPO[]
  campaigns?: Campaign[] | null
  deals?: Deal[] | null
}

// Interface for DoorProxy fields
interface DoorProxyFields {
  note_count?: number
  taglist?: Tag[] | null
  distributors?: CompanyDistributor[]
  gpos?: CompanyGPO[]
  chain_proxy?: {
    taglist?: Tag[] | null
    distributors?: CompanyDistributor[]
    gpos?: CompanyGPO[]
    campaigns?: Campaign[] | null
    deals?: Deal[] | null
  }
}

export function getCommonContactCompanyColumns<
  T extends RequiredFields | { chain_proxy: RequiredFields },
>(
  baseColumnHelper: ColumnHelper<T>,
  sorting?: DataTableColumnSort[],
  preferences?: Preferences,
  inCampaigns?: boolean,
  accessor?: string
) {
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const commonColumnHelper = createTableColumnHelper({
    columns: COMMON_COLUMNS,
    tooltips,
  })
  // NOTE: magic trick to get the correct type
  const columnHelper = baseColumnHelper as ColumnHelper<any>
  const getAccessor = (key: keyof RequiredFields) => {
    return accessor ? `${accessor}.${key}` : key
  }
  const getValueWithAccessor = (info: any) => {
    return accessor ? info.row.original[accessor] : info.row.original
  }
  const cols: any[] = [
    columnHelper.accessor(getAccessor('campaigns'), {
      ...commonColumnHelper.retrieveColumn('campaigns'),
      cell: (info) => {
        return <CampaignsCell campaigns={info.getValue()} />
      },
      size: 260,
    }),
    columnHelper.display({
      ...commonColumnHelper.retrieveColumn('sales_stages'),
      size: 260,
      cell: (info) => {
        return (
          <SaleStagesCellFromDeals
            deals={getValueWithAccessor(info)?.deals}
            sortAsc={
              sorting?.length && sorting[0].id === 'sales_stages'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),
    columnHelper.display({
      ...commonColumnHelper.retrieveColumn('account_owners'),
      size: 260,
      cell: (info) => {
        const deals = getValueWithAccessor(info)?.deals
        const accountOwnerIds = getAccountOwnerIdsFromDeals(deals)
        return (
          <AccountOwnerCell
            accountOwnerIds={accountOwnerIds}
            sortAsc={
              sorting?.length && sorting[0].id === 'account_owners'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),
    columnHelper.accessor(getAccessor('taglist'), {
      ...commonColumnHelper.retrieveColumn('taglist'),
      size: 260,
      cell: (info) => {
        const taglist = info.getValue()
        return <TaglistCell taglist={taglist} />
      },
    }),
    columnHelper.accessor(getAccessor('contact_count'), {
      ...commonColumnHelper.retrieveColumn('contact_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        return info.getValue() || '-'
      },
    }),
    columnHelper.accessor(getAccessor('note_count'), {
      ...commonColumnHelper.retrieveColumn('note_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        return info.getValue() || '-'
      },
    }),
    columnHelper.accessor(getAccessor('deal_count'), {
      ...commonColumnHelper.retrieveColumn('deal_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        return info.getValue() || '-'
      },
    }),
    columnHelper.accessor(getAccessor('distributors'), {
      ...commonColumnHelper.retrieveColumn('distributors'),
      size: 260,
      cell: (info) => {
        return (
          <DistributorsCell
            distributors={getValueWithAccessor(info)?.distributors}
          />
        )
      },
    }),
    columnHelper.accessor(getAccessor('gpos'), {
      ...commonColumnHelper.retrieveColumn('gpos'),
      size: 260,
      cell: (info) => {
        return <GPOsCell gpos={getValueWithAccessor(info)?.gpos} />
      },
    }),
  ]

  if (inCampaigns) {
    cols.push(
      columnHelper.accessor('campaign_attributed_deal_count', {
        ...commonColumnHelper.retrieveColumn('campaign_attributed_deal_count', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => {
          return info.row.original.campaign_attributed_deal_count || '-'
        },
      })
    )
  }

  return cols as AccessorKeyColumnDef<T, any>[] // NOTE: yet another magic trick
}

/**
 * Get common door proxy columns for door proxy tables
 * This function creates columns for both door proxy and chain proxy attributes
 */
export function getCommonDoorProxyColumns<T extends DoorProxyFields>(
  baseColumnHelper: ColumnHelper<T>,
  sorting?: DataTableColumnSort[],
  preferences?: Preferences,
  inCampaigns?: boolean
) {
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const commonColumnHelper = createTableColumnHelper({
    columns: COMMON_COLUMNS,
    tooltips,
  })
  // NOTE: magic trick to get the correct type
  const columnHelper = baseColumnHelper as ColumnHelper<any>

  const cols = [
    // Chain proxy campaigns
    columnHelper.accessor('chain_proxy.campaigns', {
      ...commonColumnHelper.retrieveColumn('campaigns'),
      cell: (info) => {
        return <CampaignsCell campaigns={info.getValue()} />
      },
      size: 260,
    }),

    // Sales stages from door proxy deals
    columnHelper.display({
      ...commonColumnHelper.retrieveColumn('sales_stages'),
      size: 260,
      cell: (info) => {
        return (
          <SaleStagesCellFromDeals
            deals={info.row.original.deals}
            sortAsc={
              sorting?.length && sorting[0].id === 'sales_stages'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),

    // Account owners from door proxy deals
    columnHelper.display({
      ...commonColumnHelper.retrieveColumn('account_owners'),
      size: 260,
      cell: (info) => {
        const deals = info.row.original.deals
        const accountOwnerIds = getAccountOwnerIdsFromDeals(deals)
        return (
          <AccountOwnerCell
            accountOwnerIds={accountOwnerIds}
            sortAsc={
              sorting?.length && sorting[0].id === 'account_owners'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),

    // Door proxy tags
    columnHelper.accessor('taglist', {
      ...commonColumnHelper.retrieveColumn('taglist'),
      id: 'door_taglist',
      header: 'Door Tags',
      size: 260,
      cell: (info) => {
        const taglist = info.getValue()
        return <TaglistCell taglist={taglist} />
      },
    }),

    // Chain proxy tags
    columnHelper.accessor('chain_proxy.taglist', {
      ...commonColumnHelper.retrieveColumn('taglist'),
      id: 'chain_proxy_taglist',
      header: 'Chain Tags',
      size: 260,
      cell: (info) => {
        const taglist = info.getValue()
        return <TaglistCell taglist={taglist} />
      },
    }),

    // Note count
    columnHelper.accessor('note_count', {
      ...commonColumnHelper.retrieveColumn('note_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        return info.getValue() || '-'
      },
    }),

    // Door proxy distributors
    columnHelper.accessor('distributors', {
      ...commonColumnHelper.retrieveColumn('distributors'),
      id: 'door_distributors',
      header: 'Door Distributors',
      size: 260,
      cell: (info) => {
        return <DistributorsCell distributors={info.getValue()} />
      },
    }),

    // Chain proxy distributors
    columnHelper.accessor('chain_proxy.distributors', {
      ...commonColumnHelper.retrieveColumn('distributors'),
      id: 'chain_proxy_distributors',
      header: 'Chain Distributors',
      size: 260,
      cell: (info) => {
        return <DistributorsCell distributors={info.getValue()} />
      },
    }),

    // Door proxy GPOs
    columnHelper.accessor('gpos', {
      ...commonColumnHelper.retrieveColumn('gpos'),
      id: 'door_gpos',
      header: 'Door GPOs',
      size: 260,
      cell: (info) => {
        return <GPOsCell gpos={info.getValue()} />
      },
    }),

    // Chain proxy GPOs
    columnHelper.accessor('chain_proxy.gpos', {
      ...commonColumnHelper.retrieveColumn('gpos'),
      id: 'chain_proxy_gpos',
      header: 'Chain GPOs',
      size: 260,
      cell: (info) => {
        return <GPOsCell gpos={info.getValue()} />
      },
    }),
  ]

  if (inCampaigns) {
    cols.push(
      columnHelper.accessor('chain_proxy.campaign_attributed_deal_count', {
        ...commonColumnHelper.retrieveColumn('campaign_attributed_deal_count', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => {
          return info.getValue() || '-'
        },
      })
    )
  }

  return cols as AccessorKeyColumnDef<T, any>[] // NOTE: yet another magic trick
}
